import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";

export default function Privacy() {
  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Legal</span>
              </div>
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold mb-4" data-testid="text-privacy-title">Privacy Policy</h1>
              <p className="text-sm text-muted-foreground">Last updated: March 4, 2026</p>
            </div>

            <div className="prose prose-invert prose-lg max-w-none [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p]:mb-6 [&_h2]:text-foreground [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-12 [&_h2]:mb-4 [&_h3]:text-foreground [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3 [&_ul]:text-muted-foreground [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ol]:text-muted-foreground [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6 [&_li]:mb-2 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_strong]:text-foreground [&_strong]:font-semibold">

              <h2>1. Introduction</h2>
              <p>Process Design Institute Ltd. ("we," "us," or "our"), operating at www.mattrebeiro.com, is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our Website and use our services. We are a Canadian corporation with our corporate address at 2-610 Ford Drive, Suite 225, Oakville, Ontario, L6J 7W4.</p>
              <p>This Privacy Policy complies with Canada's Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy legislation.</p>

              <h2>2. Information We Collect</h2>

              <h3>Information You Provide to Us</h3>
              <p>We may collect personal information that you voluntarily provide to us when you:</p>
              <ul>
                <li>Create an account on our Website</li>
                <li>Sign in using Google OAuth</li>
                <li>Post content in our community forums</li>
                <li>Contact us via email</li>
                <li>Subscribe to our podcast or newsletter</li>
              </ul>
              <p>This information may include:</p>
              <ul>
                <li>Name and display name</li>
                <li>Email address</li>
                <li>Profile image (when signing in with Google)</li>
                <li>Username and password (for direct registration)</li>
                <li>Any content you post in forums or community areas</li>
              </ul>

              <h3>Information Collected Automatically</h3>
              <p>When you access the Website, we may automatically collect certain information, including:</p>
              <ul>
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Referring URLs</li>
                <li>Pages viewed and time spent on pages</li>
                <li>Date and time of visits</li>
              </ul>

              <h3>Cookies and Session Data</h3>
              <p>We use session cookies to maintain your login state and provide a secure, personalized experience. These cookies are essential for the operation of the Website and cannot be disabled without affecting functionality. We do not use advertising or tracking cookies.</p>

              <h2>3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Provide, operate, and maintain the Website and its features</li>
                <li>Create and manage your user account</li>
                <li>Enable your participation in community features (forums, events, resources)</li>
                <li>Respond to your inquiries and communications</li>
                <li>Monitor and analyze usage patterns to improve the Website</li>
                <li>Protect against unauthorized access and ensure the security of the Website</li>
                <li>Comply with legal obligations</li>
              </ul>

              <h2>4. How We Share Your Information</h2>
              <p>We do not sell, rent, or trade your personal information to third parties. We may share your information in the following limited circumstances:</p>
              <ul>
                <li><strong>Service providers</strong> — We may share information with third-party service providers that perform services on our behalf, such as hosting, data storage, and authentication (e.g., Google OAuth). These providers are contractually obligated to protect your information and use it only for the purposes we specify.</li>
                <li><strong>Community content</strong> — Information you post in public areas of the Website (such as forum posts, display name, and profile image) is visible to other registered users.</li>
                <li><strong>Legal requirements</strong> — We may disclose your information if required to do so by law, court order, or governmental regulation, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.</li>
                <li><strong>Business transfers</strong> — In the event of a merger, acquisition, or sale of all or a portion of our assets, your personal information may be transferred as part of that transaction.</li>
              </ul>

              <h2>5. Data Storage and Security</h2>
              <p>Your personal information is stored on secure servers. We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
              <ul>
                <li>Encryption of passwords using industry-standard hashing algorithms</li>
                <li>SSL/TLS encryption for data in transit</li>
                <li>Session-based authentication with secure cookie handling</li>
                <li>Regular security reviews of our systems</li>
              </ul>
              <p>While we strive to protect your personal information, no method of transmission over the Internet or method of electronic storage is 100% secure. We cannot guarantee absolute security.</p>

              <h2>6. Data Retention</h2>
              <p>We retain your personal information for as long as your account is active or as needed to provide you with our services. If you wish to delete your account, please contact us at the email address below. We will delete or anonymize your personal information within a reasonable timeframe, except where we are required to retain it for legal, accounting, or reporting obligations.</p>

              <h2>7. Your Rights Under PIPEDA</h2>
              <p>Under Canada's Personal Information Protection and Electronic Documents Act, you have the right to:</p>
              <ul>
                <li><strong>Access</strong> — Request access to the personal information we hold about you</li>
                <li><strong>Correction</strong> — Request correction of inaccurate or incomplete personal information</li>
                <li><strong>Withdrawal of consent</strong> — Withdraw your consent for the collection, use, or disclosure of your personal information, subject to legal or contractual restrictions</li>
                <li><strong>Complaint</strong> — File a complaint with the Office of the Privacy Commissioner of Canada if you believe your privacy rights have been violated</li>
              </ul>
              <p>To exercise any of these rights, please contact us using the information provided below.</p>

              <h2>8. Google OAuth</h2>
              <p>If you choose to sign in using Google, we receive your name, email address, and profile image from Google. We use this information solely to create and manage your account on our Website. We do not access any other information from your Google account. Your use of Google's services is governed by Google's own Privacy Policy and Terms of Service.</p>

              <h2>9. Children's Privacy</h2>
              <p>Our Website is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete that information promptly.</p>

              <h2>10. Changes to This Privacy Policy</h2>
              <p>We may update this Privacy Policy from time to time. We will notify you of material changes by updating the "Last updated" date at the top of this page. We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.</p>

              <h2>11. Contact Us</h2>
              <p>If you have any questions or concerns about this Privacy Policy, or if you wish to exercise your privacy rights, please contact us at:</p>
              <p>
                <strong>Process Design Institute Ltd.</strong><br />
                2-610 Ford Drive, Suite 225<br />
                Oakville, Ontario, L6J 7W4<br />
                Canada<br />
                Email: <a href="mailto:advisor@mattrebeiro.com">advisor@mattrebeiro.com</a>
              </p>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
